import { zodResolver } from '@hookform/resolvers/zod'
import { useAtomValue } from 'jotai'
import { useResetAtom } from 'jotai/utils'
import React from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import * as R from 'remeda'
import { z } from 'zod'

import { ExecuteCancelDialog } from '~/components/elements/ExecuteCancelDialog'
import { LabelingContainer } from '~/components/elements/LabelingContainer'
import { StyledInput } from '~/components/elements/StyledInput'
import { VStack } from '~/components/elements/VStack'
import { useFormField } from '~/hooks/useFormField'
import { useLogEvent } from '~/hooks/useLogEvent'
import { useInvalidateSurveyForSettingsQuery } from '~/hooks/useSurveyForSettingsQuery'
import { ensureExists, reject } from '~/utils'

import { dialogInfoAtom } from '../atoms'
import { useBrandGroup } from '../hooks/useBrandGroup'
import { useCloseDialogWithRefresh } from '../hooks/useCloseDialogWithRefresh'
import { useCreateBrandMutation } from '../hooks/useCreateBrandMutation'
import { useUpdateBrandMutation } from '../hooks/useUpdateBrandMutation'

const useSchema = () => {
  const { t } = useTranslation()
  const brandGroup = useBrandGroup()
  const dialogInfo = useAtomValue(dialogInfoAtom)
  const { brand } = ensureExists(dialogInfo, t('assertion_failed'))

  return React.useMemo(
    () =>
      z.object({
        brandNumber: z
          .string()
          .min(1)
          .transform(Number)
          .refine(
            R.allPass([
              (brandNumber) => 1 <= brandNumber,
              (brandNumber) => {
                const otherBrandNumbers = R.pipe(
                  brandGroup?.brands ?? [],
                  reject(({ id }) => id === brand?.id),
                  R.map(R.prop('number')),
                )

                return !otherBrandNumbers.includes(brandNumber)
              },
            ]),
          ),
        name: z.string().min(1),
      }),
    [brand?.id, brandGroup?.brands],
  )
}

type Schema = ReturnType<typeof useSchema>
type InputValues = z.input<Schema>
type OutputValues = z.output<Schema>

export const EditBrandDialog = () => {
  const { t } = useTranslation()
  const dialogInfo = useAtomValue(dialogInfoAtom)
  const { brand, dialogType } = ensureExists(dialogInfo, t('assertion_failed'))

  const brandGroup = useBrandGroup()
  const brandNumbers = React.useMemo(
    () => (brandGroup?.brands ?? []).map(R.prop('number')),
    [brandGroup?.brands],
  )

  const schema = useSchema()
  const {
    control,
    formState: { isDirty, isValid },
    handleSubmit,
    reset,
    setFocus,
  } = useForm<InputValues>({
    defaultValues: {
      brandNumber: `${
        brand?.number ??
        (brandNumbers.length === 0 ? 1 : Math.max(...brandNumbers) + 1)
      }`,
      name: brand?.name ?? '',
    },
    resolver: zodResolver(schema),
  })
  const brandNumberField = useFormField(control, 'brandNumber')
  const nameField = useFormField(control, 'name')

  const resetDialogInfo = useResetAtom(dialogInfoAtom)
  const logEvent = useLogEvent()
  const invalidateSurveyForSettingsQuery = useInvalidateSurveyForSettingsQuery()
  const createBrandMutation = useCreateBrandMutation({
    onSuccess: (data) => {
      logEvent('Add brand')
      invalidateSurveyForSettingsQuery()
      reset({
        brandNumber: `${
          Math.max(data.createBrand?.number ?? 0, ...brandNumbers) + 1
        }`,
        name: '',
      })
      setFocus('name')
    },
  })
  const closeDialogWithRefresh = useCloseDialogWithRefresh()
  const updateBrandMutation = useUpdateBrandMutation({
    onSuccess: closeDialogWithRefresh,
  })
  const handleValid = React.useCallback<SubmitHandler<InputValues>>(
    (data_) => {
      const data = data_ as unknown as OutputValues
      if (!isDirty) {
        resetDialogInfo()

        return
      }

      if (dialogType === 'create') {
        createBrandMutation.mutate(data)
      } else {
        updateBrandMutation.mutate({ brandId: brand.id, ...data })
      }
    },
    [
      brand,
      createBrandMutation,
      dialogType,
      isDirty,
      resetDialogInfo,
      updateBrandMutation,
    ],
  )

  return (
    <ExecuteCancelDialog
      disabledExecute={!isValid}
      executing={createBrandMutation.isPending || updateBrandMutation.isPending}
      mode={dialogType === 'create' ? 'create' : 'update'}
      title={dialogType === 'create' ? t('add_brand') : t('update_brand')}
      onCancel={resetDialogInfo}
      onExecute={handleSubmit(handleValid)}
    >
      <VStack spacing={2}>
        <LabelingContainer label={t('brand_number')}>
          <StyledInput
            min={1}
            type="number"
            width="100%"
            {...brandNumberField}
          />
        </LabelingContainer>

        <LabelingContainer label={t('brand_name')}>
          <StyledInput autoFocus={true} width="100%" {...nameField} />
        </LabelingContainer>
      </VStack>
    </ExecuteCancelDialog>
  )
}
